import { useState, type ReactNode } from 'react';
import { CheckCircle, Info, Warning } from '@phosphor-icons/react';
import Modal from '@/components/molecules/Modal/Modal';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import { useOpenSupportChat } from '@/shared/support/supportChat';
import { getFormatDate } from '@/shared/utils/dates.util';
import { formatCurrency } from '@/shared/utils/general.util';
import { PLAN_CHANGE_SUPPORT_ENTRY } from '../constants';
import { usePlanChangeFlow, type PlanChangeDone } from '../hooks/usePlanChangeFlow';
import { planChangeRefusalOf, type PlanChangeRefusal } from '../lib/planChangeErrors';
import { labelPeriodicity, perPeriodOf } from '../lib/periodicity';
import type { PlanChangeRequest, PlanChangeResult } from '../lib/types';
import { usePaymentCards } from '../hooks/usePaymentCards';
import { AdjustmentCardPicker } from './AdjustmentCardPicker';

const CTA_BASE = 'min-h-12 w-full rounded-2xl px-4 py-2.5 text-base font-bold disabled:opacity-50';
const CTA_PRIMARY = cn(CTA_BASE, 'bg-h-primary-50 text-h-text-white');
const CTA_SECONDARY = cn(CTA_BASE, 'border-h-primary-50 text-h-primary-50 border bg-white');

const cents = (value: number) => formatCurrency(value, true, { isInCents: true });
const dayOf = (date: string) => getFormatDate(date, 'D [de] MMMM');

const Row = ({ label, value, strong }: { label: string; value: string; strong?: boolean }) => (
	<div className={cn('flex items-baseline justify-between gap-3', strong ? 'text-base font-bold' : 'text-sm')}>
		<span className={strong ? 'text-h-text-heading' : 'text-h-text-medium'}>{label}</span>
		<span className='text-h-text-heading tabular-nums'>{value}</span>
	</div>
);

const Header = ({ icon, title, body }: { icon: ReactNode; title: string; body?: string }) => (
	<div className='flex flex-col gap-3'>
		{icon}
		<div className='flex flex-col gap-1.5'>
			<h2 className='text-h-text-heading text-[17px] leading-snug font-bold'>{title}</h2>
			{body && <p className='text-h-text-medium text-sm leading-snug'>{body}</p>}
		</div>
	</div>
);

const IconBadge = ({ tone, children }: { tone: 'primary' | 'success' | 'warning'; children: ReactNode }) => (
	<span
		className={cn(
			'flex h-10 w-10 items-center justify-center rounded-xl',
			tone === 'primary' && 'bg-h-primary-90 text-h-primary-30',
			tone === 'success' && 'bg-h-success-90 text-h-success-30',
			tone === 'warning' && 'bg-h-warning-90 text-h-warning-30',
		)}>
		{children}
	</span>
);

const longDayOf = (date: string) => getFormatDate(date, 'D [de] MMMM [de] YYYY');

/**
 * Monthly → annual today (R1+, engine #324): the annual list price, and the year
 * it pays for, which starts when the current month ends; that month is not charged again.
 */
const AnnualBreakdown = ({ quote }: { quote: PlanChangeResult }) => (
	<div className='bg-h-bg-app flex flex-col gap-2 rounded-xl p-4'>
		<Row label={quote.to.name} value={`${cents(quote.to.list_cents)} ${perPeriodOf(quote.to)}`} />
		{quote.new_period_start && quote.new_period_end && (
			<p className='text-h-text-medium text-sm'>
				Tu año: del {dayOf(quote.new_period_start)} al {longDayOf(quote.new_period_end)}
			</p>
		)}
		<div className='border-h-neutral-90 mt-1 border-t pt-2'>
			<Row label='Pagas hoy' value={cents(quote.charge_cents)} strong />
			<p className='text-h-text-medium mt-1 text-xs'>IVA incluido</p>
		</div>
		<p className='text-h-text-medium text-xs leading-snug'>
			Tu mes actual ya está pagado, así que no se cobra otra vez: tu año empieza cuando termina.
		</p>
		<p className='text-h-text-medium text-xs leading-snug'>
			¿Prefieres esperar? Cámbiate en tu renovación del {dayOf(quote.period_end)} y ese día pagas tu año.
		</p>
	</div>
);

/** What changes today and from the renewal, line by line, as the engine computed it. */
const Breakdown = ({ quote }: { quote: PlanChangeResult }) => {
	if (quote.periodicity_change === 'to_annual') return <AnnualBreakdown quote={quote} />;
	const renewal = dayOf(quote.period_end);
	const discountPct = Math.round(100 - quote.paid_share_bp / 100);
	const isCharge = quote.outcome === 'charge' || quote.outcome === 'waived';
	return (
		<div className='bg-h-bg-app flex flex-col gap-2 rounded-xl p-4'>
			<Row label={quote.to.name} value={`${cents(quote.to.list_cents)} ${perPeriodOf(quote.to)}`} />
			<Row label={quote.from.name} value={`${cents(quote.from.list_cents)} ${perPeriodOf(quote.from)}`} />
			{isCharge && !!quote.remaining_months && (
				<Row label='Meses completos que faltan de tu año' value={`${quote.remaining_months}`} />
			)}
			{isCharge && discountPct > 0 && <Row label='Descuento que tienes este periodo' value={`${discountPct}%`} />}
			<div className='border-h-neutral-90 mt-1 border-t pt-2'>
				<Row label='Pagas hoy' value={cents(quote.charge_cents)} strong />
				{quote.outcome === 'charge' && <p className='text-h-text-medium mt-1 text-xs'>IVA incluido</p>}
			</div>
			<p className='text-h-text-medium text-xs leading-snug'>
				{quote.outcome === 'waived'
					? `La diferencia es de ${cents(quote.waived_cents)}, menos de $10, así que no te la cobramos. Desde el ${renewal} se renueva como ${quote.to.name}.`
					: `Desde tu renovación del ${renewal} se cobra como ${quote.to.name}.`}
			</p>
		</div>
	);
};

const reviewHeaderOf = (quote: PlanChangeResult) => {
	const renewal = dayOf(quote.period_end);
	if (quote.periodicity_change === 'to_annual' && quote.outcome === 'charge') {
		return {
			title: `Cambia a ${quote.to.name}`,
			body: quote.new_period_start
				? `Hoy tienes ${quote.from.name}. Pagas hoy tu año y empieza el ${dayOf(quote.new_period_start)}, cuando termina tu mes actual.`
				: `Hoy tienes ${quote.from.name}. Pagas hoy tu año completo.`,
		};
	}
	switch (quote.outcome) {
		case 'credit':
		case 'scheduled':
			return {
				title: quote.periodicity_change === 'to_monthly' ? 'Tu cambio queda para el fin de tu año' : 'Tu cambio queda para tu renovación',
				body: `Tu plan cambia a ${quote.to.name} el ${dayOf(scheduledDateOf(quote))}. Hasta entonces sigues con ${quote.from.name} y hoy no pagas nada.`,
			};
		case 'free':
			return {
				title: `Cambia a ${quote.to.name} sin costo`,
				body: `Tu periodo actual no tuvo costo, así que el cambio tampoco. Desde el ${renewal} se renueva como ${quote.to.name}.`,
			};
		case 'lateral':
			return {
				title: `Cambia a ${quote.to.name} sin costo`,
				body: `Los dos planes cuestan lo mismo. Desde el ${renewal} se renueva como ${quote.to.name}.`,
			};
		case 'waived':
			return {
				title: `Cambia a ${quote.to.name} sin costo`,
				body: `La diferencia es menor a $10, así que no te la cobramos. El cambio es inmediato.`,
			};
		case 'charge':
			return {
				title: `Cambia a ${quote.to.name}`,
				body: quote.remaining_months
					? `Hoy tienes ${quote.from.name}. Pagas la diferencia por los ${quote.remaining_months} meses completos que faltan de tu año, y el cambio es inmediato.`
					: `Hoy tienes ${quote.from.name}. Pagas la diferencia de este mes y el cambio es inmediato.`,
			};
		default:
			return { title: `Cambia a ${quote.to.name}`, body: `Hoy tienes ${quote.from.name}. El cambio es inmediato.` };
	}
};

/**
 * A downgrade only happens at the renewal (R1+). `credit` is what an engine
 * without R1+ would answer for one; the web never offers it today, it asks for
 * the renewal instead.
 */
const isAtRenewal = (quote: PlanChangeResult) => quote.outcome === 'scheduled' || quote.outcome === 'credit';
const scheduledDateOf = (quote: PlanChangeResult) => (quote.outcome === 'credit' ? quote.period_end : quote.effective_at);

const confirmLabelOf = (quote: PlanChangeResult) =>
	quote.outcome === 'charge'
		? `Pagar ${cents(quote.charge_cents)}`
		: isAtRenewal(quote)
			? `Cambiar el ${dayOf(scheduledDateOf(quote))}`
			: 'Cambiar de plan';

const doneCopyOf = (done: PlanChangeDone) => {
	const renewal = dayOf(done.period_end);
	if (done.settled === 'scheduled') {
		return {
			title: 'Tu cambio quedó programado',
			body: `Tu plan cambia a ${done.to.name} el ${dayOf(done.effective_at)}. Hasta entonces sigues con ${done.from.name}.`,
		};
	}
	if (done.settled === 'paid_pending') {
		return {
			title: 'Recibimos tu pago',
			body: `En unos minutos verás ${done.to.name} en tu cuenta.`,
		};
	}
	const title = `Listo, ya tienes ${done.to.name}`;
	if (done.periodicity_change === 'to_annual' && done.outcome === 'charge') {
		return {
			title,
			body: `Pagaste ${cents(done.charge_cents)}.${
				done.new_period_start && done.new_period_end
					? ` Tu año va del ${dayOf(done.new_period_start)} al ${longDayOf(done.new_period_end)}.`
					: ''
			}`,
		};
	}
	if (done.outcome === 'charge') {
		return {
			title,
			body: done.remaining_months
				? `Pagaste ${cents(done.charge_cents)} por los ${done.remaining_months} meses que faltan de tu año.`
				: `Pagaste ${cents(done.charge_cents)} por la diferencia de este mes. Desde el ${renewal} se renueva como ${done.to.name}.`,
		};
	}
	return { title, body: `Hoy no pagaste nada. Desde el ${renewal} se renueva como ${done.to.name}.` };
};

const RefusalContent = ({
	refusal,
	onReview,
	onClose,
	supportDraft,
}: {
	refusal: PlanChangeRefusal;
	onReview: () => void;
	onClose: () => void;
	supportDraft: string;
}) => {
	const openSupportChat = useOpenSupportChat();
	return (
		<div className='flex flex-col gap-4'>
			<Header
				icon={
					<IconBadge tone='warning'>
						<Warning size={20} weight='fill' />
					</IconBadge>
				}
				title={refusal.title}
				body={refusal.description}
			/>
			<div className='flex flex-col gap-2'>
				{refusal.next === 'requote' && (
					<button type='button' className={CTA_PRIMARY} onClick={onReview}>
						Revisar de nuevo
					</button>
				)}
				{refusal.next === 'retry' && (
					<button type='button' className={CTA_PRIMARY} onClick={onReview}>
						Intentar de nuevo
					</button>
				)}
				{refusal.next === 'support' && (
					<button
						type='button'
						className={CTA_PRIMARY}
						onClick={() => openSupportChat(PLAN_CHANGE_SUPPORT_ENTRY, supportDraft)}>
						Escríbenos
					</button>
				)}
				<button type='button' className={refusal.next === 'none' ? CTA_PRIMARY : CTA_SECONDARY} onClick={onClose}>
					{refusal.next === 'none' ? 'Entendido' : 'Ahora no'}
				</button>
			</div>
		</div>
	);
};

const PlanChangeContent = ({
	request,
	onClose,
	onChanged,
}: {
	request: PlanChangeRequest;
	onClose: () => void;
	onChanged?: () => void;
}) => {
	const flow = usePlanChangeFlow(request, onChanged);
	const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
	const { defaultCardId } = usePaymentCards();
	const quote = flow.quote && labelPeriodicity(flow.quote);
	const supportDraft = quote
		? `Hola, quiero cambiar mi plan de ${quote.from.name} a ${quote.to.name}.`
		: `Hola, quiero cambiar mi plan a ${request.target.displayName}.`;

	if (flow.isQuoting || (!quote && !flow.quoteRefusal)) {
		return (
			<div className='flex flex-col items-center gap-3 py-8'>
				<Spinner className='size-6' />
				<p className='text-h-text-medium text-sm'>Calculando tu cambio</p>
			</div>
		);
	}
	if (flow.quoteRefusal || !quote) {
		return (
			<RefusalContent
				refusal={flow.quoteRefusal ?? planChangeRefusalOf(null)}
				onReview={flow.review}
				onClose={onClose}
				supportDraft={supportDraft}
			/>
		);
	}
	if (flow.step === 'refused' && flow.refusal) {
		return <RefusalContent refusal={flow.refusal} onReview={flow.review} onClose={onClose} supportDraft={supportDraft} />;
	}
	if (flow.step === 'processing') {
		return (
			<div className='flex flex-col items-center gap-3 py-8'>
				<Spinner className='size-6' />
				<p className='text-h-text-medium text-sm'>
					{quote.outcome === 'charge' ? 'Procesando tu pago' : 'Cambiando tu plan'}
				</p>
			</div>
		);
	}
	if (flow.step === 'done' && flow.done) {
		const copy = doneCopyOf(labelPeriodicity(flow.done));
		return (
			<div className='flex flex-col gap-4'>
				<Header
					icon={
						<IconBadge tone='success'>
							<CheckCircle size={20} weight='fill' />
						</IconBadge>
					}
					title={copy.title}
					body={copy.body}
				/>
				<button type='button' className={CTA_PRIMARY} onClick={onClose}>
					Listo
				</button>
			</div>
		);
	}

	const header = reviewHeaderOf(quote);
	const cardId = selectedCardId ?? defaultCardId;
	const needsCard = quote.outcome === 'charge';
	return (
		<div className='flex flex-col gap-4'>
			<Header
				icon={
					<IconBadge tone='primary'>
						<Info size={20} weight='fill' />
					</IconBadge>
				}
				title={header.title}
				body={header.body}
			/>
			{!isAtRenewal(quote) && <Breakdown quote={quote} />}
			{needsCard && <AdjustmentCardPicker selectedCardId={cardId} onSelect={(card) => setSelectedCardId(card.id)} />}
			<div className='flex flex-col gap-2'>
				<button
					type='button'
					className={CTA_PRIMARY}
					disabled={needsCard && !cardId}
					onClick={() =>
						quote.outcome === 'credit'
							? flow.confirm({ timing: 'renewal' })
							: flow.confirm({ tokenId: cardId ?? undefined })
					}>
					{confirmLabelOf(quote)}
				</button>
				{quote.periodicity_change === 'to_annual' && quote.outcome === 'charge' && (
					<button type='button' className={CTA_SECONDARY} onClick={() => flow.confirm({ timing: 'renewal' })}>
						Cambiar el {dayOf(quote.period_end)}
					</button>
				)}
				<button type='button' className={CTA_SECONDARY} onClick={onClose}>
					Ahora no
				</button>
			</div>
		</div>
	);
};

/**
 * The plan change, quote to result, in one modal (a bottom sheet on mobile).
 * Opening it with a `request` quotes right away; `null` keeps it closed.
 * Closing it mid-charge is safe: the payment still settles in the engine, and a
 * second change while it is in flight is refused there.
 */
export const PlanChangeModal = ({
	request,
	onClose,
	onChanged,
}: {
	request: PlanChangeRequest | null;
	onClose: () => void;
	/** The change landed (applied, scheduled or paid): the surface refreshes what it shows. */
	onChanged?: () => void;
}) => (
	<Modal open={!!request} onClose={onClose} sx='w-[94%] !max-w-md'>
		{request && (
			<PlanChangeContent
				key={`${request.subscriptionId}:${request.target.productVersionId}`}
				request={request}
				onClose={onClose}
				onChanged={onChanged}
			/>
		)}
	</Modal>
);
